import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../helpers/firebase";
import { getAuth } from "firebase/auth";

/**
 * AI Automation Model
 * Handles all Leo AI automation activities and data
 */

export const aiAutomationModel = {
  /**
   * Get automation statistics for the current user
   */
  async getAutomationStats() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log("[AI Automation] Getting stats for user:", user.uid);

      // Query Firestore for leo actions
      const actionsRef = collection(db, "leoActions");
      const q = query(
        actionsRef,
        where("ownerId", "==", user.uid),
        where("result.data.status", "==", "completed")
      );

      console.log("[AI Automation] Querying leoActions collection...");
      const querySnapshot = await getDocs(q);
      console.log(
        "[AI Automation] Found",
        querySnapshot.docs.length,
        "completed actions"
      );

      const actions = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        console.log("[AI Automation] Action data:", {
          id: doc.id,
          actionType: data.actionData?.actionType,
          status: data.result?.data?.status,
          ownerId: data.ownerId,
        });
        return {
          id: doc.id,
          ...data,
        };
      });

      // Calculate stats from actions based on actionData.actionType
      const stats = {
        messagesSent: actions.filter(
          (a) => a.actionData?.actionType === "message_sent"
        ).length,
        remindersSent: actions.filter(
          (a) => a.actionData?.actionType === "reminder_sent"
        ).length,
        maintenanceRequests: actions.filter(
          (a) => a.actionData?.actionType === "maintenance_request"
        ).length,
        lateFeesApplied: actions.filter(
          (a) => a.actionData?.actionType === "late_fee_applied"
        ).length,
        depositsProcessed: actions.filter(
          (a) => a.actionData?.actionType === "deposit_processed"
        ).length,
        screeningCompleted: actions.filter(
          (a) => a.actionData?.actionType === "screening_completed"
        ).length,
      };

      console.log("[AI Automation] Calculated stats:", stats);
      return stats;
    } catch (error) {
      console.error("Error getting automation stats:", error);
      throw error;
    }
  },

  /**
   * Get AI automation activities from this week
   */
  async getRecentActivities(limitCount = 10) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log(
        "[AI Automation] Getting recent activities for user:",
        user.uid
      );

      // Calculate start of this week (Monday)
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
      startOfWeek.setHours(0, 0, 0, 0);

      console.log("[AI Automation] Querying activities from:", startOfWeek);

      // Query Firestore for leo actions from this week
      const actionsRef = collection(db, "leoActions");
      const q = query(
        actionsRef,
        where("ownerId", "==", user.uid),
        where("createdAt", ">=", Timestamp.fromDate(startOfWeek)),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );

      console.log(
        "[AI Automation] Querying leoActions for recent activities..."
      );
      const querySnapshot = await getDocs(q);
      console.log(
        "[AI Automation] Found",
        querySnapshot.docs.length,
        "recent activities"
      );

      const activities = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        console.log("[AI Automation] Recent activity data:", {
          id: doc.id,
          actionType: data.actionData?.actionType,
          description: data.description,
          entityName: data.entityName,
          createdAt: data.createdAt,
        });
        return {
          id: doc.id,
          type: data.actionData?.actionType || "unknown",
          action: data.description || "Action completed",
          tenant: data.entityName || "Unknown Tenant",
          unit: data.propertyAddress || "Unknown Door",
          time: this.formatTimeAgo(data.createdAt),
          status: data.result?.data?.status || "completed",
          icon: this.getActivityIcon(data.actionData?.actionType),
          details: data.result?.data || null,
        };
      });

      console.log("[AI Automation] Processed activities:", activities);
      return activities;
    } catch (error) {
      console.error("Error getting recent activities:", error);
      throw error;
    }
  },

  /**
   * Get pending actions that require user attention
   */
  async getPendingActions() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log(
        "[AI Automation] Getting pending actions for user:",
        user.uid
      );

      // Query Firestore for pending leo actions
      const actionsRef = collection(db, "leoActions");
      const q = query(
        actionsRef,
        where("ownerId", "==", user.uid),
        where("result.data.status", "==", "pending"),
        orderBy("priority", "desc"),
        orderBy("createdAt", "desc")
      );

      console.log("[AI Automation] Querying leoActions for pending actions...");
      const querySnapshot = await getDocs(q);
      console.log(
        "[AI Automation] Found",
        querySnapshot.docs.length,
        "pending actions"
      );

      const actions = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        console.log("[AI Automation] Pending action data:", {
          id: doc.id,
          actionType: data.actionData?.actionType,
          description: data.description,
          status: data.result?.data?.status,
          priority: data.priority,
        });
        return {
          id: doc.id,
          type: data.actionData?.actionType || "unknown",
          title: data.description || "Action pending",
          description: data.description || "Action requires attention",
          priority: data.priority || "medium",
          action: data.result?.data?.suggestedAction || "Review and approve",
          tenant: data.entityName || "Unknown Tenant",
          unit: data.propertyAddress || "Unknown Door",
          details: data.result?.data || null,
          createdAt: data.createdAt,
        };
      });

      console.log("[AI Automation] Processed pending actions:", actions);
      return actions;
    } catch (error) {
      console.error("Error getting pending actions:", error);
      throw error;
    }
  },

  /**
   * Debug method to get all leoActions for the current user (for debugging)
   */
  async getAllLeoActions() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log("[AI Automation] Getting ALL leoActions for user:", user.uid);

      // Query Firestore for ALL leo actions (no filters)
      const actionsRef = collection(db, "leoActions");
      const q = query(actionsRef, where("ownerId", "==", user.uid));

      console.log("[AI Automation] Querying ALL leoActions...");
      const querySnapshot = await getDocs(q);
      console.log(
        "[AI Automation] Found",
        querySnapshot.docs.length,
        "total actions"
      );

      const actions = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        console.log("[AI Automation] Raw action data:", {
          id: doc.id,
          ownerId: data.ownerId,
          actionData: data.actionData,
          result: data.result,
          description: data.description,
          entityName: data.entityName,
          propertyAddress: data.propertyAddress,
          createdAt: data.createdAt,
          priority: data.priority,
        });
        return {
          id: doc.id,
          ...data,
        };
      });

      console.log("[AI Automation] All actions:", actions);
      return actions;
    } catch (error) {
      console.error("Error getting all leo actions:", error);
      throw error;
    }
  },
  async getAIInsights() {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      // Query Firestore for AI insights
      const insightsRef = collection(db, "aiInsights");
      const q = query(
        insightsRef,
        where("ownerId", "==", user.uid),
        where("status", "==", "active"),
        orderBy("createdAt", "desc"),
        limit(5)
      );

      const querySnapshot = await getDocs(q);
      const insights = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          type: data.type,
          title: data.title,
          description: data.description,
          action: data.recommendedAction || "View details",
          icon: this.getInsightIcon(data.type),
          priority: data.priority || "medium",
          details: data.details || null,
        };
      });

      return insights;
    } catch (error) {
      console.error("Error getting AI insights:", error);
      throw error;
    }
  },

  /**
   * Execute an AI automation action
   */
  async executeAction(actionId, actionType, actionData = {}) {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not authenticated");
      }

      // Create automation activity record
      const activityData = {
        ownerId: user.uid,
        type: actionType,
        action: actionData.action || "AI automation executed",
        tenantName: actionData.tenantName || null,
        unitNumber: actionData.unitNumber || null,
        status: "completed",
        details: actionData.details || null,
        createdAt: serverTimestamp(),
        executedAt: serverTimestamp(),
      };

      const activitiesRef = collection(db, "aiAutomationActivities");
      const docRef = await addDoc(activitiesRef, activityData);

      // Update pending action status if it exists
      if (actionId) {
        const actionRef = doc(db, "aiPendingActions", actionId);
        await updateDoc(actionRef, {
          status: "completed",
          completedAt: serverTimestamp(),
          completedBy: "ai_automation",
        });
      }

      return docRef.id;
    } catch (error) {
      console.error("Error executing AI action:", error);
      throw error;
    }
  },

  /**
   * Format time ago for display
   */
  formatTimeAgo(timestamp) {
    if (!timestamp) return "Unknown time";

    const now = new Date();
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) {
      return "Just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? "s" : ""} ago`;
    }
  },

  /**
   * Get icon for activity type
   */
  getActivityIcon(type) {
    const icons = {
      message_sent: "MessageOutlined",
      reminder_sent: "ClockCircleOutlined",
      maintenance_request: "ToolOutlined",
      late_fee_applied: "DollarOutlined",
      deposit_processed: "BankOutlined",
      screening_completed: "SafetyOutlined",
      lease_renewal: "FileTextOutlined",
      eviction: "ExclamationCircleOutlined",
      // Legacy types for backward compatibility
      message: "MessageOutlined",
      reminder: "ClockCircleOutlined",
      maintenance: "ToolOutlined",
      late_fee: "DollarOutlined",
      deposit: "BankOutlined",
      screening: "SafetyOutlined",
    };
    return icons[type] || "InfoCircleOutlined";
  },

  /**
   * Get icon for insight type
   */
  getInsightIcon(type) {
    const icons = {
      cashflow: "DollarOutlined",
      maintenance: "ToolOutlined",
      tenant: "UserOutlined",
      compliance: "SafetyOutlined",
      market: "BarChartOutlined",
      efficiency: "ThunderboltOutlined",
    };
    return icons[type] || "InfoCircleOutlined";
  },
};

export default aiAutomationModel;
